// Une ligne de la liste, dépliable (porté de FeedbackRow). Chaque geste
// s'affiche selon les permissions renvoyées par l'API (can_*), jamais
// recalculées ici.
import type { JSX } from 'preact'
import { useRef, useState } from 'preact/hooks'
import type { FeedbackItem, FeedbackStatus } from '@feedbaacks/sdk'
import { useFeedbaacks } from '../context'
import { Archive, ArchiveRestore, ChevronDown, ChevronRight, History, MapPin, Message, Pencil, Trash } from '../icons'
import { Avatar, IconButton, useDismiss } from '../primitives'
import { absoluteDate, cx, describePageUrl, errorMessage, formatRelative, memo } from '../util'
import { AgentRoute } from './AgentRoute'
import { Detail } from './Detail'
import { EditHistoryList } from './parts'
import { AgentChip, SourcePill, StatusPill, TypeLabel } from './pills'
import { Vote } from './Vote'

export interface RowProps {
  item: FeedbackItem
  expanded: boolean
  onToggle: (id: string) => void
  onChange: (next: FeedbackItem) => void
  onRemove: (id: string) => void
}

function RowBase({ item, expanded, onToggle, onChange, onRemove }: RowProps): JSX.Element {
  const { client, t, locale, notify, confirm, store, options } = useFeedbaacks()
  const contextLabel = options.contextLabel ? options.contextLabel(item) : typeof item.context?.label === 'string' ? item.context.label : null
  const origin = contextLabel ?? describePageUrl(item.page_url)
  const hasActions = item.can_edit || item.can_delete || item.can_archive || item.can_approve_agent

  // Chaque geste : mise à jour optimiste si possible, retour arrière sinon.
  const act = async (run: () => Promise<FeedbackItem | void>, optimistic?: FeedbackItem) => {
    const prev = item
    if (optimistic) onChange(optimistic)
    try {
      const next = await run()
      if (next) {
        onChange(next)
        options.onEvent?.({ type: 'updated', item: next })
      }
    } catch (err) {
      onChange(prev)
      notify('error', errorMessage(err, t('action_failed')))
    }
  }

  const setStatus = (status: FeedbackStatus) => void act(() => client.feedback.setStatus(item.id, status), { ...item, status })
  const toggleArchive = () => void act(() => (item.is_archived ? client.feedback.unarchive(item.id) : client.feedback.archive(item.id)))
  const setAgent = (approved: boolean) =>
    void act(() => client.feedback.setAgentApproval(item.id, approved), { ...item, agent_approved: approved, handled_by: approved ? 'agent' : undefined })
  const sendToTower = (project: string) => void act(() => client.feedback.sendToTower(item.id, project))
  const remove = async () => {
    if (!(await confirm({ title: t('delete_title'), message: t('delete_message'), confirmLabel: t('delete'), destructive: true }))) return
    try {
      await client.feedback.remove(item.id)
      onRemove(item.id)
      options.onEvent?.({ type: 'deleted', id: item.id })
    } catch (err) {
      notify('error', errorMessage(err, t('action_failed')))
    }
  }

  return (
    <div class={cx('fb-row', expanded && 'is-expanded', `fb-type-${item.type}`)} data-testid="feedbaacks-row" data-expanded={expanded} data-id={item.id}>
      <span class="fb-row-ribbon" aria-hidden="true" />
      <Vote item={item} onChange={onChange} />
      <div
        class="fb-row-header"
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        data-testid="feedbaacks-row-header"
        onClick={() => onToggle(item.id)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onToggle(item.id)
          }
        }}
      >
        <div class="fb-row-body">
          <h3 class="fb-row-title">
            {item.is_unread && !expanded && <span class="fb-unread-dot" aria-label={t('unread')} />}
            {item.title}
          </h3>
          <div class="fb-row-meta">
            <TypeLabel type={item.type} />
            <span onClick={(e) => e.stopPropagation()}>
              <StatusPill status={item.status} canChange={item.can_change_status} onChange={setStatus} />
            </span>
            <SourcePill source={item.source} />
            {item.handled_by === 'tower' ? <AgentChip by="tower" /> : item.agent_approved && !item.can_approve_agent && <AgentChip />}
            {item.is_archived && (
              <span class="fb-pill fb-archived" data-testid="feedbaacks-archived-badge">
                <Archive size={10} />
                {t('archived')}
              </span>
            )}
            <span class="fb-faint">•</span>
            <span class="fb-author">
              <Avatar name={item.author?.name ?? '?'} src={item.author?.avatar_url} />
              <span class="fb-clamp-1">{item.author?.name || t('anonymous')}</span>
            </span>
            {origin && (
              <>
                <span class="fb-faint">•</span>
                <span class="fb-origin" title={item.page_url} data-testid="feedbaacks-row-origin">
                  <MapPin size={12} />
                  <span class="fb-clamp-1">{origin}</span>
                </span>
              </>
            )}
            {options.slots?.rowMeta?.(item)}
          </div>
        </div>
        {hasActions && (
          <div class={cx('fb-row-actions-hover', expanded && 'is-visible')} onClick={(e) => e.stopPropagation()}>
            {item.can_edit && (
              <IconButton label={t('edit')} testId="feedbaacks-row-edit" onClick={() => store.set({ composerOpen: true, editingId: item.id, selectedId: null })}>
                <Pencil size={12} />
              </IconButton>
            )}
            {item.can_approve_agent && item.handled_by !== 'tower' && <AgentRoute item={item} onApprove={setAgent} onTower={sendToTower} />}
            {item.can_archive && (
              <IconButton label={t(item.is_archived ? 'unarchive' : 'archive')} testId="feedbaacks-row-archive" onClick={toggleArchive}>
                {item.is_archived ? <ArchiveRestore size={12} /> : <Archive size={12} />}
              </IconButton>
            )}
            {item.can_delete && (
              <IconButton label={t('delete')} danger testId="feedbaacks-row-delete" onClick={() => void remove()}>
                <Trash size={12} />
              </IconButton>
            )}
          </div>
        )}
        <div class="fb-row-side">
          {item.comments_count > 0 && (
            <span class="fb-row-comments">
              <Message size={12} />
              {item.comments_count}
              {item.unread_comments_count > 0 && !expanded && <span class="fb-unread-dot is-badge" aria-label={t('unread_comments')} />}
            </span>
          )}
          <Timestamp createdAt={item.created_at} relative={formatRelative(item.created_at, locale, t)} title={absoluteDate(item.created_at, locale)} history={item.edit_history} />
        </div>
        <span class="fb-row-chevron">{expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</span>
      </div>
      {expanded && (
        <div class="fb-row-detail">
          <Detail item={item} onChange={onChange} />
        </div>
      )}
    </div>
  )
}

/** Heure de la ligne ; quand le retour a été modifié, elle ouvre l'historique. */
function Timestamp({ relative, title, history }: { createdAt: string; relative: string; title: string; history?: FeedbackItem['edit_history'] }): JSX.Element {
  const { t } = useFeedbaacks()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLSpanElement>(null)
  useDismiss(open, () => setOpen(false), ref)
  if (!history?.length) return <span class="fb-mono fb-time" title={title}>{relative}</span>
  return (
    <span class="fb-popover-anchor" ref={ref}>
      <button type="button" class="fb-mono fb-time is-link" title={`${title} · ${t('history_count', { count: history.length })}`} aria-expanded={open}
        aria-label={t('history_open')}
        onClick={(e) => {
          e.stopPropagation()
          setOpen((v) => !v)
        }}>
        <History size={10} />
        {relative}
      </button>
      {open && (
        <div class="fb-popover" role="dialog" aria-label={t('history_title')} onClick={(e) => e.stopPropagation()}>
          <div class="fb-section-title">{t('history_title')} · {history.length}</div>
          <EditHistoryList history={history} />
        </div>
      )}
    </span>
  )
}

export const Row = memo(RowBase)

export function RowSkeleton(): JSX.Element {
  return (
    <div class="fb-row is-skeleton" aria-hidden="true">
      <div class="fb-vote"><span class="fb-skel" /></div>
      <div class="fb-row-body">
        <span class="fb-skel is-wide" />
        <span class="fb-skel" />
      </div>
    </div>
  )
}
